/**
 * Shared utility functions for the landing page v2 feature
 */

import type { ComponentType, CtaButton, PageComponent } from "../_types";
import { DEFAULT_NAV_LABELS, SINGLE_USE_COMPONENT_TYPES } from "./constants";
import { isEmailLink } from "./smart-link-utils";

/**
 * Strips all HTML tags from a string, correctly decoding entities like &amp; → &
 * Uses the browser's built-in DOMParser (client-only — all callers are "use client" components).
 * @param html - Raw HTML string
 * @returns Plain text with no HTML tags or encoded entities
 */
export function stripHtmlTags(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

/**
 * Validates that a URL uses a safe protocol (http or https)
 * Rejects data: URIs, javascript: URIs, and invalid URLs
 * @param url - The URL string to validate
 * @returns true if the URL uses http: or https: protocol
 */
export function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Returns true for components that should appear in nav-like surfaces (navbar, footer "sections" column).
 * Footer, dividers, and any explicitly-hidden sections are excluded.
 *
 * Divider is excluded structurally rather than left to its `hiddenFromNav: true` default, because
 * that default is one toggle away from being flipped — by the existing generic "show in
 * navigation" control, or by the copilot, for which `hiddenFromNav` is a writable base field. A
 * divider has nothing to navigate *to*: `DEFAULT_NAV_LABELS.divider` is the empty string, so
 * `getAnchorId` returns null and the resulting link renders as a blank `href="#null"`, and two
 * dividers in the nav collide on that same null React key. Same reasoning that already excludes
 * footer — a section with no addressable content doesn't belong in navigation at all.
 * @param c - A page component
 * @returns Whether the component should be linkable from navigation
 */
export function isNavigableSection(c: { type: ComponentType; hiddenFromNav?: boolean }): boolean {
  return canAppearInNav(c.type) && !c.hiddenFromNav;
}

/**
 * Whether a section type can ever appear in navigation, ignoring its own `hiddenFromNav`.
 *
 * Split out from {@link isNavigableSection} so the builder UI can ask the question without a
 * component instance: the section options menu offers a "Hide from navigation" toggle, and for a
 * type that's excluded structurally that control writes a field nothing reads — it appears to work,
 * changes the label, and does nothing. Gating the control on the same rule that decides nav
 * membership is what keeps the two from disagreeing.
 * @param type - A component type.
 * @returns Whether sections of this type are eligible for navigation at all.
 */
export function canAppearInNav(type: ComponentType): boolean {
  return type !== "footer" && type !== "divider";
}

/**
 * Validates that a URL is an HTTPS Supabase Storage URL.
 * Use for any user-provided URL that is rendered as an image/asset on the public page —
 * prevents CSS/img injection via attacker-controlled hosts and enforces HTTPS for content
 * embedded on landing pages.
 *
 * Guards `typeof` explicitly before parsing: the `string` param type is only a compile-time
 * promise for values arriving from a server action's request body or a copilot tool call, and
 * `new URL(...)` coerces its argument with `String(...)` — so `["https://evil.example/x.png"]`
 * would otherwise parse and pass, then break whichever caller treats it as a string.
 *
 * Known gap: this accepts any `*.supabase.co` subdomain, not only this deployment's own project,
 * so a URL on someone else's free Supabase project still passes and every visitor to that page
 * requests the third-party host. Narrowing it to `NEXT_PUBLIC_SUPABASE_URL`'s hostname needs a
 * check of which hosts existing stored pages actually reference first: this same validator gates
 * autosave, so any page holding a URL from another project would both stop rendering that image
 * and stop saving. Tracked in ENG-1084.
 *
 * @param url - The URL to validate. Typed `string`, but treated as untrusted at runtime.
 * @returns true if the URL is a string, HTTPS, and points at *.supabase.co
 */
export function isValidStorageUrl(url: string): boolean {
  if (typeof url !== "string") return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname.endsWith(".supabase.co");
  } catch {
    return false;
  }
}

/** Dangerous URI protocols that must never be opened */
const DANGEROUS_PROTOCOLS = /^(javascript|data|vbscript):/i;

/**
 * Checks if a URL is testable in a new tab (external, anchor, or relative path).
 * Used for the "Test Link" affordance in builder editors.
 * Rejects empty strings and dangerous protocols (javascript:, data:, vbscript:).
 * @param url - The URL to check
 * @returns Whether the URL can be safely opened in a new tab for testing
 */
export function isTestableUrl(url: string): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (DANGEROUS_PROTOCOLS.test(trimmed)) return false;
  return true;
}

/**
 * Resolves a link href for the "Test Link" in builder editors.
 * Anchor links (#section) are prefixed with the public page slug so they open the correct page.
 * @param href - The raw link value from the editor
 * @param parentSlug - The parent page slug from builder context
 * @returns The href to use for the test link anchor tag
 */
export function resolveTestLinkHref(href: string, parentSlug: string): string {
  if (href.startsWith("#")) {
    return `/${parentSlug}${href}`;
  }
  return href;
}

/**
 * Converts a label string to a URL-safe anchor ID.
 * Lowercases, replaces non-alphanumeric sequences with hyphens, trims leading/trailing hyphens.
 * @param text - The label text to convert
 * @returns URL-safe anchor string
 */
export function generateAnchorId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Returns the anchor ID for a component, or null for navbar.
 * All types derive from navigationLabel when set.
 * Single-use types (hero, faq, venue, schedule, activities) fall back to their type name.
 * Multi-use types (richText, textButton, carousel) fall back to DEFAULT_NAV_LABELS.
 * @param component - The page component to get the anchor for
 * @returns Anchor ID string (without #) or null for navbar
 */
export function getAnchorId(component: PageComponent): string | null {
  if (component.type === "navbar") return null;

  const label =
    component.navigationLabel ||
    (SINGLE_USE_COMPONENT_TYPES.includes(component.type) ? null : DEFAULT_NAV_LABELS[component.type]);
  if (!label) {
    return SINGLE_USE_COMPONENT_TYPES.includes(component.type) ? component.type : null;
  }

  return generateAnchorId(label);
}

/**
 * Returns a unique anchor ID for a component within its page.
 * If multiple components share the same base anchor, suffixes are added: register, register-1, register-2.
 * @param component - The component to get the anchor for
 * @param components - All page components (order matters — first occurrence keeps the base ID)
 * @returns Unique anchor ID string (without #) or null for navbar
 */
export function getUniqueAnchorId(component: PageComponent, components: PageComponent[]): string | null {
  const base = getAnchorId(component);
  if (!base) return null;

  let count = 0;
  for (const c of components) {
    if (c.id === component.id) break;
    if (getAnchorId(c) === base) count++;
  }

  return count === 0 ? base : `${base}-${count}`;
}

/**
 * Whether an `#anchor` CTA link no longer resolves to any section on the page.
 *
 * Anchor ids are derived from each component's mutable `navigationLabel` (see
 * {@link getUniqueAnchorId}), so renaming a section silently invalidates every stored
 * `#old-label` link. The builder uses this to flag such links at authoring time instead
 * of shipping a CTA that scrolls nowhere.
 *
 * Only `#`-prefixed values are considered; everything else (forms, pages, external URLs)
 * returns `false` since this check can't reason about them. Email links (`#__email-{id}`) share
 * the `#` prefix but aren't section anchors, so they return `false` too.
 *
 * @param value - The stored link value (e.g. `#register`).
 * @param knownAnchorValues - The `#anchor` values currently resolvable on the page
 *   (i.e. `getUniqueAnchorId`-derived ids, each prefixed with `#`).
 * @returns true when `value` is an anchor link with no matching section.
 */
export function isUnresolvedAnchorLink(value: string, knownAnchorValues: string[]): boolean {
  if (typeof value !== "string" || !value.startsWith("#")) return false;
  if (isEmailLink(value)) return false;
  return !knownAnchorValues.includes(value);
}

/**
 * Builds the `#old` → `#new` map of section anchors that moved between two component arrays.
 *
 * Anchor ids derive from each component's mutable `navigationLabel` and are de-duplicated by
 * position (see {@link getUniqueAnchorId}), so renaming one section can also renumber the
 * suffix of another that shared its base label ("Register"/"Register 1" → `#register`/
 * `#register-1`). Diffing per component (matched by `id`) before and after the edit captures
 * both the renamed section and any others whose anchor shifted as a side effect.
 *
 * Anchor ids are unique within each array, so the returned map is a bijection — safe to apply
 * as a single-pass substitution (see {@link remapAnchorLinks}) without chaining.
 *
 * @param before - Components before the edit.
 * @param after - Components after the edit (same ids; one or more `navigationLabel`s changed).
 * @returns Map of `#old` → `#new` for every component whose resolved anchor changed; empty
 *   when nothing moved.
 */
export function buildAnchorRemap(before: PageComponent[], after: PageComponent[]): Record<string, string> {
  const remap: Record<string, string> = {};
  const beforeById = new Map(before.map((c) => [c.id, c]));

  for (const component of after) {
    const prev = beforeById.get(component.id);
    if (!prev) continue;

    const oldId = getUniqueAnchorId(prev, before);
    const newId = getUniqueAnchorId(component, after);
    if (oldId && newId && oldId !== newId) remap[`#${oldId}`] = `#${newId}`;
  }

  return remap;
}

/**
 * Rewrites stored `#anchor` CTA/link values so they keep pointing at their section after a
 * rename. Applies {@link buildAnchorRemap}'s output as a single-pass substitution: a value is
 * replaced only when it exactly matches a remapped `#old` anchor. Non-anchor links (forms,
 * pages, external URLs) and anchors that didn't move are left untouched.
 *
 * Covers every structured field the builder's LinkPicker can write an anchor into: CTA button urls
 * (hero, text & button, navbar), the text-button/venue `buttonLink`, the deprecated single-CTA/logo
 * links, pricing card CTAs, card-grid hrefs, and footer logo/column links. Component types whose
 * only link fields are external-by-nature (team member socials, sponsor `websiteUrl`, footer
 * social links) are intentionally omitted — those are never section anchors.
 *
 * Out of scope: `#anchor` links authored inside rich-text HTML via SmartLinkPopover (RichText
 * `content` and the various TipTap `description`/`answer` fields). Those live in HTML strings, not
 * structured fields, and — like the LinkPicker's own broken-anchor flag — this rewrite doesn't
 * reach them; a rename can still orphan an in-content anchor link. Tracked as a follow-up. This gap
 * is landing-page-v2-only: the email-builder counterpart
 * (`src/lib/email/scope/rewrite-page-slug-links.ts`) covers its rich-text Text block via
 * `rewriteTextHtmlLinks`, which operates on HTML directly.
 *
 * Also called with page-path remaps (not just anchors) via the slug-rename cascade
 * (`_shared/slug-rename-cascade.ts`'s `buildPagePathRemap`/`cascadePageSlugRename`) — the
 * substitution below is a generic exact-string match that doesn't know or care whether a key is an
 * anchor or a page path.
 *
 * @param components - The current components (not mutated).
 * @param remap - `#old` → `#new` map from {@link buildAnchorRemap}.
 * @returns A new components array with rewritten links, or the same reference when `remap` is
 *   empty.
 */
export function remapAnchorLinks(components: PageComponent[], remap: Record<string, string>): PageComponent[] {
  if (Object.keys(remap).length === 0) return components;

  const sub = <T extends string | undefined>(value: T): T =>
    typeof value === "string" && Object.hasOwn(remap, value) ? (remap[value] as T) : value;

  const subButtons = (buttons?: CtaButton[]): CtaButton[] | undefined =>
    buttons?.map((b) => ({ ...b, url: sub(b.url) }));

  return components.map((component) => {
    switch (component.type) {
      case "hero":
        return { ...component, ctaButtons: subButtons(component.ctaButtons) };
      case "textButton":
        return { ...component, ctaButtons: subButtons(component.ctaButtons), buttonLink: sub(component.buttonLink) };
      case "venue":
        return { ...component, buttonLink: sub(component.buttonLink) };
      case "navbar":
        return {
          ...component,
          ctaButtons: subButtons(component.ctaButtons),
          ctaLink: sub(component.ctaLink),
          logoLink: sub(component.logoLink),
        };
      case "pricing":
        return { ...component, items: component.items?.map((item) => ({ ...item, ctaLink: sub(item.ctaLink) })) };
      case "cardGrid":
        return { ...component, items: component.items?.map((item) => ({ ...item, href: sub(item.href) })) };
      case "footer":
        return {
          ...component,
          logoLink: sub(component.logoLink),
          columns: component.columns?.map((col) => ({
            ...col,
            links: col.links.map((link) => ({ ...link, url: sub(link.url) })),
          })),
        };
      default:
        return component;
    }
  });
}

/**
 * Returns a navigation label that won't create a duplicate anchor among existing components.
 * If the label's anchor already exists, appends a numeric suffix: "Register" → "Register 1".
 * @param label - The desired navigation label
 * @param components - Current page components to check against
 * @returns The original label if unique, or a suffixed version
 */
export function makeNavLabelUnique(label: string, components: PageComponent[]): string {
  const baseAnchor = generateAnchorId(label);
  const existingAnchors = new Set(components.map((c) => getAnchorId(c)).filter(Boolean));

  if (!existingAnchors.has(baseAnchor)) return label;

  let suffix = 1;
  while (existingAnchors.has(`${baseAnchor}-${suffix}`)) suffix++;
  return `${label} ${suffix}`;
}

/**
 * Checks if an anchor ID would be unique among all current components.
 * @param components - Current page components
 * @param candidateAnchor - The anchor ID to check (without #)
 * @param excludeComponentId - Component ID to exclude (the one being edited)
 * @returns true if the anchor is unique (or null), false if duplicate
 */
export function isAnchorUnique(
  components: PageComponent[],
  candidateAnchor: string | null,
  excludeComponentId: string
): boolean {
  if (!candidateAnchor) return true;
  return !components.some((c) => c.id !== excludeComponentId && getAnchorId(c) === candidateAnchor);
}

/**
 * Splits an item count into balanced row sizes, capped by maxColumns.
 * Extra cards are assigned to later rows so 5 items render as 2 + 3, not 3 + 2.
 *
 * @param itemCount - Number of items to place
 * @param maxColumns - Maximum cards per row
 * @returns Row sizes whose sum equals itemCount
 */
export function getBalancedRowSizes(itemCount: number, maxColumns: number): number[] {
  const count = Math.max(0, Math.floor(itemCount));
  if (count === 0) return [];

  const safeMaxColumns = Math.max(1, Math.floor(maxColumns));
  const rowCount = Math.ceil(count / safeMaxColumns);
  const baseRowSize = Math.floor(count / rowCount);
  const largerRowCount = count % rowCount;

  return Array.from({ length: rowCount }, (_, index) =>
    index >= rowCount - largerRowCount ? baseRowSize + 1 : baseRowSize
  );
}

/**
 * Formats a past ISO timestamp as a short relative label ("just now", "5m ago", "2h ago", ...).
 * Minute-level granularity — for a live autosave indicator that needs second-level precision, see
 * `SaveStatusIndicator`'s own local formatter instead; that's a deliberately different tier, not
 * duplication of this one.
 * @param iso - An ISO 8601 timestamp in the past.
 * @returns A short relative-time label.
 */
export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
